import { useMemo, useContext } from 'react';
import { usePageContext } from 'vike-react/usePageContext';
import SavedStateContext from '../../../contexts/SavedStateContext';
import { useInitialApplicationStateByJobId } from '../../../contexts/InitialApplicationStateContext';
import { isToolsPage } from '../../../utils/routing/route-utils';
import { useUserState } from '../../../query/UserProvider';
import { extractJobsFromUserState } from '../../../utils/user-state/extract-jobs-from-user-state';
import { type ApplicationState } from '../../../types/tracked-job';
import type { DisplayJob } from '../../../types';
import type { UserStateMap } from '../../../types/user-state';

export interface HitState {
  jobId: string;
  isSaved: boolean;
  isViewed: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  isToolsPage: boolean;
  savedJobPayload: DisplayJob & { id: string };
  /** ISO timestamp the job was applied to, when known — drives the "Applied · 5d ago" SRP stage chip. */
  appliedAt?: string;
}

export interface UseHitStateProps {
  hit: DisplayJob;
}

/**
 * Hook to manage all state for a Hit component
 * Consolidates Redux state, SavedStateContext, and pageContext
 */
export function useHitState({ hit }: UseHitStateProps): HitState {
  const savedState = useContext(SavedStateContext);
  const jobId = String(hit.objectID || '');

  // TEC-404: derive viewed/applied/saved jobs from the TanStack `userState` cache instead of the
  // Redux `jobs` slice — same derivation `extractJobsFromUserState` already uses for SSR
  // hydration (redux/createStore.ts), so this matches the old Redux selectors' output exactly for
  // an equivalent UserStateMap. `savedJobs` now reads from here too: its writer
  // (`hooks/useSavedJobActions.ts`) dual-writes into this cache as well as Redux, so this stays in
  // sync with a real save + reload the same way `viewedJobs`/`appliedJobs` already do.
  const { data: userStateData } = useUserState();
  const { viewedJobs, savedJobs, appliedJobs } = useMemo(
    () => extractJobsFromUserState(userStateData ?? {}),
    [userStateData]
  );
  const userStateEntries = userStateData ?? {};

  const isSavedFromUserState = savedJobs.some((job) => job.id === jobId);
  const isViewedFromUserState = viewedJobs.some((job) => job.id === jobId);
  const isAppliedFromUserState = appliedJobs.some((job) => job.id === jobId);

  // Fallback to SavedStateContext for SSR hydration compatibility
  const isSaved = isSavedFromUserState || (savedState?.saved?.has(jobId) || false);
  const isViewed = isViewedFromUserState || (savedState?.viewed?.has(jobId) || false);
  const isApplied = isAppliedFromUserState || (savedState?.applied?.has(jobId) || false);
  
  // Get pageContext at the top level (hooks must be called at top level)
  let pageContext: ReturnType<typeof usePageContext> | null = null;
  try {
    pageContext = usePageContext();
  } catch {
    // PageContext not available, continue without it
  }
  
  // Get all jobs from Redux
  const allJobs = useMemo(() => [
    ...viewedJobs,
    ...savedJobs,
    ...appliedJobs,
  ], [viewedJobs, savedJobs, appliedJobs]);
  
  const job = useMemo(() => allJobs.find((j) => j.id === jobId), [allJobs, jobId]);
  
  // Get applicationState from Redux job first (most up-to-date), then appliedJob# entry, then pageContext, then island initial data
  const appliedItemKey = `appliedJob#${jobId}` as const;
  const appliedEntry = userStateEntries[appliedItemKey];
  const initialApplicationStateByJobId = useInitialApplicationStateByJobId();
  
  // Also check pageContext.userState during SSR as a fallback
  const pageContextUserState: UserStateMap | undefined = pageContext 
    ? (pageContext as any)?.userState as UserStateMap | undefined
    : undefined;
  
  const currentApplicationState: ApplicationState | null = useMemo(() => {
    const fromJob = job?.applicationState;
    const fromEntry = (appliedEntry?.data as Record<string, unknown>)?.applicationState as ApplicationState | undefined;
    const fromPageContext = (pageContextUserState?.[appliedItemKey]?.data as Record<string, unknown>)?.applicationState as ApplicationState | undefined;
    const fromInitial = initialApplicationStateByJobId?.[jobId];
    return (fromJob ?? fromEntry ?? fromPageContext ?? (fromInitial as ApplicationState | undefined) ?? null) as ApplicationState | null;
  }, [job?.applicationState, appliedEntry, pageContextUserState, appliedItemKey, initialApplicationStateByJobId, jobId]);
  
  // Check if job is hidden (independent flag, not part of ApplicationState)
  const isHidden = job?.isHidden ?? false;

  const appliedAt = typeof job?.appliedAt === 'string' ? job.appliedAt : undefined;
  
  // Check if we're on a tools page
  const pathname = pageContext 
    ? (pageContext as any)?.urlPathname || (typeof window !== 'undefined' ? window.location.pathname : '')
    : (typeof window !== 'undefined' ? window.location.pathname : '');
  
  const isToolsPageValue = useMemo(() => {
    return isToolsPage(pathname);
  }, [pathname]);
  
  // Create saved job payload
  const savedJobPayload = useMemo(() => ({ ...hit, id: hit.objectID }), [hit]);
  
  return {
    jobId,
    isSaved,
    isViewed,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage: isToolsPageValue,
    savedJobPayload,
    appliedAt,
  };
}
